import _ from "lodash";

import { CurrentProject } from "@/hooks/use-project";
import { ConsentManagementSlug } from "../consent-management/consentManagement";
import { AccountMetadata, LocalStorageProject } from "../reducers/environments";
import {
  DestinationConnectionMode,
  LoadedConsentManagementInfo,
  LoadedDestinationInfo,
} from "../types/global";
import { isAccountInHIPAAMode } from "../util";
import { areAllRequiredPropertiesAllowed } from "./DestinationPropertyMetadata";
import { SaaSDestination } from "./destinations";
import { getAudienceDestinationValidity } from "./utils/getAudienceDestinationValidity";

export enum DestinationValidity {
  valid,
  partiallyInvalid,
  totallyInvalid,
  totallyInvalidClientModeWhenHIPAA,
  totallyInvalidMissingAllowlistWhenHIPAA,
  totallyInvalidTermsOfServiceTestFailed,
  totallyInvalidAudienceConnectionTestFailed,
  totallyInvalidDisabled,
}

export interface DestinationValidationResult {
  validity: DestinationValidity;
  warningMessage?: string;
}

function doesValueMatchCondition(
  value: unknown,
  conditionValue: string | string[],
): boolean {
  if (Array.isArray(conditionValue)) {
    return conditionValue.includes(value as string);
  }

  return value === conditionValue;
}

function getConfigFieldControlValue(
  destination: SaaSDestination,
  settings: Record<string, unknown>,
  fieldKey: string,
) {
  const field = destination.configFields.find(({ key }) => key === fieldKey);
  if (!field) {
    return settings[fieldKey];
  }

  const rawValue =
    settings[fieldKey] !== undefined ? settings[fieldKey] : field.defaultValue;
  if (field.mapConfigValueToControlValue) {
    return field.mapConfigValueToControlValue(rawValue, settings);
  }

  return rawValue;
}

function isConditionSatisfied(
  destination: SaaSDestination,
  settings: Record<string, unknown>,
  condition?: { field: string; value: string | string[] },
): boolean {
  if (!condition) {
    return false;
  }

  const value = getConfigFieldControlValue(
    destination,
    settings,
    condition.field,
  );
  return doesValueMatchCondition(value, condition.value);
}

function getRequiredConfigKeys(
  destination: SaaSDestination,
  settings: Record<string, unknown>,
) {
  return _.chain(destination.configFields)
    .filter(
      ({ required, requiredWhen }) =>
        required || isConditionSatisfied(destination, settings, requiredWhen),
    )
    .map(({ key }) => key)
    .value();
}

// isDestinationFullyConfigured checks whether all of the destination's required
// config fields have been set up and a config actually exists
export function isDestinationFullyConfigured(
  configuredDest: LoadedDestinationInfo,
  destination: SaaSDestination,
): boolean {
  const settings = configuredDest?.settings || {};
  return (
    configuredDest &&
    configuredDest.id !== undefined &&
    _.every(
      getRequiredConfigKeys(destination, settings),
      (key) => key in settings,
    )
  );
}

// isDestinationPartiallyConfigured checks whether at least one of the
// destination's required config fields has been set up and a config actually exists
// if a dest doesnt have any required config fields, we skip that part of the check
export function isDestinationPartiallyConfigured(
  configuredDest: LoadedDestinationInfo,
  destination: SaaSDestination,
): boolean {
  const settings = configuredDest?.settings || {};
  const requiredConfigKeys = getRequiredConfigKeys(destination, settings);

  return (
    configuredDest &&
    configuredDest.id !== undefined &&
    (requiredConfigKeys.length === 0 ||
      _.some(requiredConfigKeys, (key) => key in settings))
  );
}

// Returns true iff 'dest' is valid for an event coming from 'source'.
export function destinationIsValidForSource(
  dest: LoadedDestinationInfo,
  source: DestinationConnectionMode,
): boolean {
  if (dest.connection_mode === "server") {
    // If the connection mode is server-side, the destination only needs to be valid server-side.
    return dest.is_valid_server_side;
  }

  if (!dest.is_valid_client_side) {
    // If the destination is specified as client-side, the destination must be valid client-side.
    return false;
  }

  // Either the event must be from the client, or the server-side implementation must be valid.
  return source === "client" || dest.is_valid_server_side;
}

export function destinationIsValid(
  configuredDest: LoadedDestinationInfo,
  destMetadata: SaaSDestination,
): boolean {
  const isValidClientSide = destinationIsValidForSource(
    configuredDest,
    "client",
  );
  const isValidServerSide = destinationIsValidForSource(
    configuredDest,
    "server",
  );

  return (
    isDestinationFullyConfigured(configuredDest, destMetadata) &&
    (isValidClientSide || isValidServerSide)
  );
}

// If consent management is configured, every destination must have a consent category.
function destinationIsValidForConsentManagement(
  consentManagementConfigs:
    | Record<ConsentManagementSlug, LoadedConsentManagementInfo>
    | undefined,
  destName: string,
): boolean {
  if (
    consentManagementConfigs === undefined ||
    _.isEmpty(consentManagementConfigs)
  ) {
    return true;
  }

  for (const config of Object.values(consentManagementConfigs)) {
    if (!config.enabled) {
      continue;
    }

    if (!config.activePublishedVersion) {
      continue;
    }

    const category = config.activePublishedVersion.consentMapping[destName];
    if (category === undefined || category === "") {
      return false;
    }
  }

  return true;
}

export function getDestinationValidity(
  accountMetadata: AccountMetadata,
  project: CurrentProject,
  configuredDest: LoadedDestinationInfo,
  destination: SaaSDestination,
  consentManagementConfigs?:
    | Record<ConsentManagementSlug, LoadedConsentManagementInfo>
    | undefined,
): DestinationValidationResult {
  if (!configuredDest.enabled) {
    return {
      validity: DestinationValidity.totallyInvalidDisabled,
    };
  }

  if (!isDestinationPartiallyConfigured(configuredDest, destination)) {
    return {
      validity: DestinationValidity.valid,
    };
  }

  if (
    isAccountInHIPAAMode(accountMetadata) &&
    configuredDest.connection_mode === "client" &&
    !configuredDest.has_baa
  ) {
    return {
      validity: DestinationValidity.totallyInvalidClientModeWhenHIPAA,
      warningMessage:
        "This destination's configuration is invalid because the connection mode is 'client' and PHI is being restricted.",
    };
  }

  const isValidClientSide = destinationIsValidForSource(
    configuredDest,
    "client",
  );
  const isValidServerSide = destinationIsValidForSource(
    configuredDest,
    "server",
  );
  if (!isValidClientSide && !isValidServerSide) {
    let invalidWarning: string;
    invalidWarning = "This destination's configuration is invalid.";

    if (!isDestinationFullyConfigured(configuredDest, destination)) {
      invalidWarning += " Required fields are missing.";
    }
    return {
      validity: DestinationValidity.totallyInvalid,
      warningMessage: invalidWarning,
    };
  } else if (!isValidClientSide) {
    return {
      validity: DestinationValidity.partiallyInvalid,
      warningMessage:
        "This destination's configuration is only valid for server-side events.",
    };
  } else if (!isValidServerSide) {
    return {
      validity: DestinationValidity.partiallyInvalid,
      warningMessage:
        "This destination's configuration is only valid for client-side events.",
    };
  }

  if (destination.destinationCategories.includes("Audiences")) {
    // there's a bit of business logic for non-Audience destinations
    // where we check if it's valid when a consent management configuration
    // is given but when we display a status indicator on either the integrations
    // list page, we don't reach that control flow. In addition, we don't
    // pass a consent management config for Audience integration overview pages
    // though, we could in the future.
    return getAudienceDestinationValidity(
      isAccountInHIPAAMode(accountMetadata),
      project,
      accountMetadata,
      configuredDest,
      destination,
    );
  }

  // it's not a great practice to do type assertion like this as
  // it can masks potential type errors but we check if a project is loaded
  // at call site so we can assume it's a LocalStorageProject for this case
  const currentProject = project as LocalStorageProject;
  if (
    !areAllRequiredPropertiesAllowed(
      accountMetadata,
      currentProject,
      configuredDest,
    )
  ) {
    return {
      validity: DestinationValidity.totallyInvalidMissingAllowlistWhenHIPAA,
      warningMessage:
        "This destination's configuration is invalid because required properties have not been added to the HIPAA allowlist.",
    };
  }

  if (
    consentManagementConfigs &&
    configuredDest.enabled &&
    !destinationIsValidForConsentManagement(
      consentManagementConfigs,
      configuredDest.name,
    )
  ) {
    return {
      validity: DestinationValidity.valid,
      warningMessage:
        "Warning: This destination may not be receiving events because it's missing a consent management category.",
    };
  }

  return {
    validity: DestinationValidity.valid,
  };
}

export function isDestinationInstrumented(
  destinationInfo: LoadedDestinationInfo,
): boolean {
  const nonInstrumentedDestinations: string[] = [
    "clearbit-enrichment",
    "one-signal",
    "zendesk",
  ];
  return !nonInstrumentedDestinations.includes(destinationInfo.type_slug);
}
